import {
  CardBrandRow,
  FinancialRow,
  PaymentBreakdownRow,
  ProductMixRow,
  ProductRow,
  TransactionCardRow,
  VerticalReport,
} from "./types";

interface VendorConfig {
  name: string;
  revenue_share?: number | null; // percentage 0–100
  flat_fee?: number | null;       // cents
}

export const formatMoney = (cents: number) =>
  (cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });

export const formatPercent = (value: number) =>
  `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

export function processData(
  financialRows: FinancialRow[],
  productRows: ProductMixRow[],
  cardRows: TransactionCardRow[],
  vendor: VendorConfig
): VerticalReport {
  const sum = (key: keyof FinancialRow) =>
    financialRows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

  const grossCredit = sum("gross_credit_sales");
  const grossRfid = sum("gross_rfid_sales");
  const grossCashBalance = sum("gross_cash_balance_sales");
  const grossCash = sum("gross_cash_sales");
  const grossToken = sum("gross_token_sales");
  const grossPromo = sum("gross_promo_balance_sales");

  const grossSales = sum("total_gross_sales");
  const tax = sum("total_tax");
  const tips = sum("total_tips");
  const discounts = sum("total_discounts") + sum("total_promo_discounts");
  const refunds = sum("total_refunds");
  const transactionCount = sum("total_transaction_count");

  const revenueSharePct = (vendor.revenue_share ?? 0) / 100;
  const revenueShareCut = Math.round(grossSales * revenueSharePct);
  const flatFee = vendor.flat_fee ?? 0;

  const netSales = grossSales - tax - discounts - refunds - revenueShareCut - flatFee;

  const payments: PaymentBreakdownRow[] = [
    { label: "Credit",         amount: grossCredit,      share: 0 },
    { label: "RFID wristband", amount: grossRfid,        share: 0 },
    { label: "Cash balance",   amount: grossCashBalance, share: 0 },
    { label: "Cash",           amount: grossCash,        share: 0 },
    { label: "Promo balance",  amount: grossPromo,       share: 0 },
    { label: "Tokens",         amount: grossToken,       share: 0 },
  ]
    .map((p) => ({ ...p, share: grossSales > 0 ? p.amount / grossSales : 0 }))
    .filter((p) => p.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  // Aggregate product mix by pos_name (the same product can appear across days/locations).
  const productMap = new Map<string, ProductRow>();
  for (const row of productRows) {
    const key = row.pos_name;
    const existing = productMap.get(key);
    const lineGross = Number(row.total_gross_sales) || 0;
    const lineTax = Number(row.total_tax) || 0;
    const lineDiscounts = Number(row.total_discounts) || 0;
    if (existing) {
      existing.unitsSold += Number(row.total_units_sold) || 0;
      existing.grossSales += lineGross;
      existing.tax += lineTax;
      existing.discounts += lineDiscounts;
      existing.netSales = existing.grossSales - existing.tax - existing.discounts;
    } else {
      productMap.set(key, {
        posName: row.pos_name,
        description: row.description,
        category: row.product_type || "Uncategorized",
        unitsSold: Number(row.total_units_sold) || 0,
        grossSales: lineGross,
        tax: lineTax,
        discounts: lineDiscounts,
        netSales: lineGross - lineTax - lineDiscounts,
      });
    }
  }
  const products = Array.from(productMap.values()).sort((a, b) => b.grossSales - a.grossSales);

  // Card brand breakdown — only the rows that came through a card processor
  // (credit + RFID, since RFID top-ups also flow through the card brand network).
  const brandMap = new Map<string, CardBrandRow>();
  for (const r of cardRows) {
    const paymentType = String(r.payment_type ?? "").toLowerCase();
    if (paymentType !== "credit" && paymentType !== "rfid") continue;
    const brand = (r.card_type || "").trim() || "Unknown";
    const existing = brandMap.get(brand);
    const amount = Number(r.transaction_amount) || 0;
    if (existing) {
      existing.count += 1;
      existing.amount += amount;
    } else {
      brandMap.set(brand, { brand, count: 1, amount });
    }
  }
  const cardBrands = Array.from(brandMap.values()).sort((a, b) => b.amount - a.amount);

  return {
    vendorName: vendor.name,
    eventName: financialRows[0]?.event_name ?? "",
    transactionCount,
    grossSales,
    tax,
    tips,
    discounts,
    refunds,
    revenueShareCut,
    flatFee,
    netSales,
    payments,
    cardBrands,
    products,
  };
}
